import React from "react";
import {
  Box, Button, Paper, Typography, alpha,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AndroidIcon from "@mui/icons-material/Android";
import { useGetLatestAndroidReleaseQuery } from "../../../Api/attoDeskApi";
import {
  NAVY, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const formatBytes = (b?: number | null): string => {
  if (!b) return "—";
  if (b < 1_048_576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1_048_576).toFixed(1)} MB`;
};

const AndroidDownload: React.FC = () => {
  const { data, isLoading } = useGetLatestAndroidReleaseQuery();
  const release = (data?.data as any) || null;

  return (
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
      display: "flex", alignItems: "center", justifyContent: "center", p: 4,
    }}>
      <Paper elevation={0} sx={{
        p: 5, borderRadius: 4, maxWidth: 540, width: "100%",
        background: "#fff", boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
      }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: 2,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha("#3DDC84", 0.18)} 0%, ${alpha("#3DDC84", 0.06)} 100%)`,
            color: "#3DDC84", mb: 2,
          }}>
            <AndroidIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: NAVY, letterSpacing: "-0.02em" }}>
            VM Display Player
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mt: 0.5 }}>
            Android signage player APK
          </Typography>
        </Box>

        {isLoading && (
          <Typography sx={{ textAlign: "center", py: 4, color: TEXT_SECONDARY }}>
            Loading…
          </Typography>
        )}

        {!isLoading && !release && (
          <Paper elevation={0} sx={{
            p: 3, textAlign: "center", border: `1px solid #fecaca`, bgcolor: "#fef2f2", borderRadius: 2,
          }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#991b1b", mb: 0.5 }}>
              No release available
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#7f1d1d" }}>
              Ask your VM Display administrator to upload an APK first.
            </Typography>
          </Paper>
        )}

        {release && (
          <>
            <Box sx={{
              p: 3, borderRadius: 2, mb: 3,
              border: `1px solid ${BORDER}`, bgcolor: "#f8fafc",
            }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                  textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Version
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, fontFamily: "monospace" }}>
                  {release.version_name}
                  <Typography component="span" sx={{ ml: 1, fontSize: 11, color: TEXT_SECONDARY }}>
                    (code {release.version_code})
                  </Typography>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                  textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Size
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>
                  {formatBytes(release.file_size)}
                </Typography>
              </Box>
              {release.released_at && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Released
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY }}>
                    {new Date(release.released_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>

            {release.release_notes && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                  textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                  Release notes
                </Typography>
                <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY, whiteSpace: "pre-wrap" }}>
                  {release.release_notes}
                </Typography>
              </Box>
            )}

            <Button fullWidth variant="contained" disableElevation
              size="large" href={release.download_url}
              startIcon={<DownloadIcon />}
              sx={{
                textTransform: "none", fontWeight: 700, py: 1.5, fontSize: 16,
                borderRadius: 2,
                bgcolor: "#3DDC84", color: "#0b1e2d",
                "&:hover": { bgcolor: "#2eb96d" },
              }}>
              Download APK
            </Button>

            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 2, textAlign: "center" }}>
              SHA-256: <code style={{ fontSize: 10 }}>{release.checksum?.slice(0, 32)}…</code>
            </Typography>

            <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: alpha(GOLD_SOFT, 0.08),
              border: `1px solid ${alpha(GOLD_SOFT, 0.3)}` }}>
              <Typography sx={{ fontSize: 12, color: TEXT_PRIMARY, lineHeight: 1.5 }}>
                <strong>Fire TV / Fire Stick:</strong> install the Downloader
                app, then paste this page's URL. <br />
                <strong>Android TV / T95:</strong> open this URL in the device's
                browser and tap "Download" to install.
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AndroidDownload;
